import { DocTypes } from "../../common/constants/DocTypes";

/**
 * Pure decision helpers for the "fetch prior intake analysis on transaction open"
 * gate in DataEntryDetails.componentDidUpdate.
 *
 * Extracted into its own module (imports only DocTypes) so the gate can be
 * unit-tested in isolation, deliberately avoiding DataEntryDetails' heavy,
 * node:stream-tainted import chain (services/Network etc.) that prevents a
 * mounted-component test suite from loading locally - same rationale as
 * intakeAutoTrigger.ts.
 */
export interface IntakeFetchGateInputs {
    /** featureFlags.IntakeIntelligence */
    readonly intakeIntelligenceEnabled: boolean;
    /** !!global.userID */
    readonly hasUser: boolean;
    /** transactionId (e.g. "1000008-42") */
    readonly transactionId?: string;
    /** getTransactionDocumentsAPIStatus === API_SUCCESS - documents for THIS tx have loaded */
    readonly documentsLoaded: boolean;
    /** transactionDetails?.transactionStatus?.id (StatusIndicatorID); null/undefined until loaded */
    readonly transactionStatusId?: number | null;
    /** the loaded documents list */
    readonly documents?: ReadonlyArray<{ recType?: string }>;
}

/** StatusIndicatorID boundary: the Data Entry bucket is everything below this. */
const DATA_ENTRY_STATUS_CEILING = 50;

/**
 * Whether enough state has loaded to make the once-per-transaction intake
 * decision. Gated on the documents list having loaded for THIS transaction so
 * `documents` isn't stale from the transaction we navigated away from, AND on the
 * transaction status being known so the Data-Entry-stage gate below can be applied
 * without permanently skipping a transaction whose status arrives after its
 * documents (AB#35099). If the status is not yet known, defer the decision.
 */
export function isIntakeAnalysisDecisionReady(i: IntakeFetchGateInputs): boolean {
    return i.intakeIntelligenceEnabled
        && i.hasUser
        && Boolean(i.transactionId)
        && i.documentsLoaded
        && i.transactionStatusId != null;
}

/**
 * Whether to fetch any prior intake analysis on transaction open. Intake
 * Intelligence is only relevant while a transaction is still in Data Entry
 * (StatusIndicatorID < 50); a transaction already in Record Attachment / Facility
 * Action / QA (>= 50) must NOT trigger the read (AB#35099, reported by Chris
 * Dustin) - this mirrors the Quick Entry surface's own isQuickEntryDataEntryStatus
 * gate. A REQUEST (REQ) document must also exist, else there is no analysis to
 * retrieve.
 */
export function shouldFetchExistingIntakeAnalysis(i: IntakeFetchGateInputs): boolean {
    const inDataEntry = i.transactionStatusId != null && i.transactionStatusId < DATA_ENTRY_STATUS_CEILING;
    const hasRequestDocument = i.documents?.some(doc => doc.recType === DocTypes.REQ) ?? false;
    return inDataEntry && hasRequestDocument;
}
